// CreateLocationEntry.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Select from 'react-select';
import { FixedSizeList as List } from 'react-window';
import './CreateLocationEntry.css';

const CreateLocationEntry = () => {
  const navigate = useNavigate();
  const { shippingID: initialShippingID } = useParams();
  const [shippingID, setShippingID] = useState(initialShippingID || '');
  const [shippingIDs, setShippingIDs] = useState([]);
  const [location, setLocation] = useState('');
  const [locations, setLocations] = useState([]);
  const [datePlaced, setDatePlaced] = useState('');
  const [notes, setNotes] = useState('');
  const [qty, setQty] = useState('');
  const [receivedDate, setReceivedDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Success states to replicate the same success page
  const [isSuccess, setIsSuccess] = useState(false);
  const [createdEntryID, setCreatedEntryID] = useState(null);

  // 1. Set initial state and reliably track resizing
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 700);

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth < 700);
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await axios.get(`${apiBaseUrl}/api/locations`);
        const options = response.data.map((loc) => ({
          value: loc.LocationName,
          label: loc.LocationName,
        }));
        setLocations(options);
      } catch (error) {
        console.error('Error fetching locations:', error);
      }
    };

    const fetchShippingIDs = async () => {
      try {
        const response = await axios.get(`${apiBaseUrl}/api/shipping-ids`);
        const options = response.data.map((id) => ({
          value: id.ShippingID,
          label: id.ShippingID,
        }));
        setShippingIDs(options);

        if (initialShippingID) {
          const itemResponse = await axios.get(`${apiBaseUrl}/api/shipping-items/${initialShippingID}`);
          const item = itemResponse.data;
          const receivedDateFormatted = new Date(item.Rcvd_Date).toISOString().split('T')[0];
          setReceivedDate(receivedDateFormatted);
        }
      } catch (error) {
        console.error('Error fetching shipping IDs:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLocations();
    fetchShippingIDs();
  }, [apiBaseUrl, initialShippingID]);

  const handleSave = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);

    const datePlacedOnly = new Date(datePlaced).toISOString().split('T')[0];
    const receivedDateOnly = new Date(receivedDate).toISOString().split('T')[0];

    if (datePlacedOnly < receivedDateOnly) {
      alert('Date Placed cannot be before the Shipping ID Received Date');
      setIsSubmitting(false);
      return;
    }

    const formData = {
      Location: location,
      DatePlaced: datePlacedOnly,
      Notes: notes,
      Qty: qty,
    };

    try {
      // 1) Update location entry
      await axios.put(`${apiBaseUrl}/api/location-entry/${shippingID}`, formData);

      // 2) Get the highest EntryID for this ShippingID
      const highestEntryIDResponse = await axios.get(`${apiBaseUrl}/api/location-entry/highest/${shippingID}`);
      const highestEntryID = highestEntryIDResponse.data.EntryID;

      if (!highestEntryID) {
        throw new Error('Server did not return a valid EntryID.');
      }

      // 3) Show success page instead of an alert
      setCreatedEntryID(highestEntryID);
      setIsSuccess(true);
    } catch (error) {
      console.error('Error updating location entry:', error.response ? error.response.data : error.message);
      alert('Error updating location entry');
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/shipping');
  };

  const customComponents = {
    MenuList: (props) => {
      const { options, children, maxHeight, getValue } = props;
      const height = 35;
      const [value] = getValue() || [];
      const initialOffset = options.indexOf(value) * height;

      return (
        <List
          height={maxHeight}
          itemCount={children.length}
          itemSize={height}
          initialScrollOffset={initialOffset}
        >
          {({ index, style }) => <div style={style}>{children[index]}</div>}
        </List>
      );
    },
  };

  const handleShippingIDChange = async (selectedOption) => {
    setShippingID(selectedOption ? selectedOption.value : '');
    if (selectedOption) {
      try {
        const response = await axios.get(`${apiBaseUrl}/api/shipping-items/${selectedOption.value}`);
        const item = response.data;
        const receivedDateFormatted = new Date(item.Rcvd_Date).toISOString().split('T')[0];
        setReceivedDate(receivedDateFormatted);
      } catch (error) {
        console.error('Error fetching shipping item details:', error);
        alert('Error fetching shipping item details');
      }
    } else {
      setReceivedDate('');
    }
  };

  const handleLocationChange = (newValue) => {
    setLocation(newValue ? newValue.value : '');
  };

  const formatDate = (dateStr) => {
    const [year, month, day] = dateStr.split('-');
    return `${month}/${day}/${year}`;
  };

  const formatInputDate = (dateStr) => {
    const [year, month, day] = dateStr.split('-');
    return `${year}-${month}-${day}`;
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  // --- SUCCESS PAGE (similar to CreateShippingItem) ---
  if (isSuccess) {
    return (
      <div className="success-container">
        <div className="checkmark"></div>
        <h2>Location Entry Updated Successfully</h2>
        <button
          className="continue-button"
          onClick={() => navigate(`/view-location-entry/${createdEntryID}`)}
        >
          Continue
        </button>
      </div>
    );
  }

  // Standard Select
  const LocationSelect = Select;

  return (
    <div className="create-container">
      <h1>Add a new item location</h1>
      <form onSubmit={handleSave}>
        <div className="form-group">
          <label>Shipping ID</label>
          <Select
            options={shippingIDs}
            onChange={handleShippingIDChange}
            value={shippingIDs.find((option) => option.value === shippingID)}
            placeholder="Select a shipping ID"
            isClearable
            isSearchable
            components={customComponents}
            required
            isDisabled={isSubmitting}
          />
        </div>
        {shippingID && (
          <div className="form-group">
            <label>Received Date: {formatDate(receivedDate)}</label>
          </div>
        )}
        <div className="form-group">
          <label>Item Location</label>
          <LocationSelect
            options={locations}
            onChange={handleLocationChange}
            value={locations.find((option) => option.value === location)}
            placeholder="Select a location"
            isClearable
            isSearchable
            required
            isDisabled={isSubmitting}
          />
        </div>
        <div className="form-group">
          <label>Date Placed</label>
          <input
            type="date"
            value={datePlaced ? formatInputDate(datePlaced) : ''}
            onChange={(e) => setDatePlaced(e.target.value)}
            required
            disabled={isSubmitting}
          />
        </div>
        <div className="form-group">
          <label>Notes (Optional)</label>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={isSubmitting}
          />
        </div>
        <div className="form-group">
          <label>Item Quantity</label>
          <input
            type="number"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            required
            disabled={isSubmitting}
          />
        </div>


        <div className="form-buttons">
          <button type="submit" disabled={isSubmitting}>
            Create and Open Entry
          </button>
          <button type="button" onClick={handleCancel} disabled={isSubmitting}>
            Cancel
          </button>
        </div>
      </form>

      {isSubmitting && (
        <div className="overlay">
          <div className="spinner"></div>
        </div>
      )}
    </div>
  );
};

export default CreateLocationEntry;
